import { forwardRef, PropsWithoutRef, useImperativeHandle } from 'react';
import { FormInputField, FormTextareaField, createZodFormResolver, lt, useFormControls } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { PaymentFormProps } from '../types';
import { PaymentAmountSection } from '../../../common/PaymentAmountSection';
import { ExternalPaymentTypeSelectField } from '../../../common/ExternalPaymentTypeSelectField';
import { useDefaultFormContext } from '../useDefaultFormValues';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { ExternalPaymentFormValues, externalPaymentFormSchema, ExternalPaymentFormField } from './schema';
import { useCreateExternalPayment } from './hooks';

export const ExternalPaymentForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const { defaultFormValues } = useDefaultFormContext();

        const defaultValues = {
            ...defaultFormValues,
            type: '',
            externalIdentifier: '',
            notes: '',
        };

        const resolver = createZodFormResolver<ExternalPaymentFormValues>(externalPaymentFormSchema);

        const { mutate } = useCreateExternalPayment({ accountId, onSuccess, onSubmitting, onSubmitted });
        const { renderFormContent, hasDirtyFields, submit } = useFormControls<ExternalPaymentFormValues>({
            onSubmit: ({ customerId, ...other }) => mutate({ accountId: customerId, ...other }),
            defaultValues,
            resolver,
        });

        useImperativeHandle(ref, () => ({
            submit,
            hasDirtyFields,
        }));

        return renderFormContent(
            <Stack spacing="300" vertical>
                <PaymentCommonSection />
                <ExternalPaymentTypeSelectField />
                <FormInputField
                    name={ExternalPaymentFormField.ExternalIdentifier}
                    label={lt('ExternalIdentifier')}
                    helper={lt('ProcessPaymentExternalPaymentsOtherPaymentExternalIdentifierHint')}
                />
                <PaymentAmountSection />
                <FormTextareaField
                    name={ProcessPaymentFormField.Notes}
                    rows={1}
                    label={lt('Notes')}
                    helper={lt('ProcessPaymentExternalPaymentsOtherPaymentNotesHint')}
                />
            </Stack>
        );
    }
);
